import React, {useState, useEffect, useRef} from 'react';
import {getSearchResults} from '../../common/services';
import {sendRecoSearch} from '../../common/services';
import ProductGrid from '../components/ProductGrid';
import {LoadingSpinner} from "../../LoadingIndicator";

/**
 * SearchResultsContainer component
 * Fetches search results and renders the ProductGrid component
 */
const SearchResultsContainer = ({
                                    loading,
                                    isDataLoaded,
                                    setLoading,
                                    setIsDataLoaded,
                                    hasScrolled,
                                    setHasScrolled,
                                    searchQuery,
                                    apiBaseUrl,
                                    clientKey,
                                    accessKey,
                                    accessSecret,
                                    currencyCode,
                                    roundingEnabled,
                                    hold,
                                    onResultsReady,
                                    historySelection
                                }) => {
    const [searchResults, setSearchResults] = useState([]);
    const [currentKeyword, setCurrentKeyword] = useState(searchQuery);
    const [currentHold, setCurrentHold] = useState(hold);
    const [containerHeight, setContainerHeight] = useState(400);
    const latestKeywordRef = useRef(currentKeyword);
    const previousProcessedKeywordRef = useRef('');
    const isRequestInProgressRef = useRef(false);
    const containerRef = useRef(null);

    useEffect(() => {

        const calculateHeight = () => {
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                const viewportHeight = window.innerHeight;
                const containerTop = rect.top;
                const availableHeight = viewportHeight - containerTop - 20;
                const minHeight = 300;
                const maxHeight = Math.max(viewportHeight * 0.6, 400);
                const calculatedHeight = Math.max(minHeight, Math.min(availableHeight, maxHeight));
                setContainerHeight(calculatedHeight);
            }
        };

        calculateHeight();

        const handleResize = () => {
            calculateHeight();
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', calculateHeight);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', calculateHeight);
        };
    }, []);

    useEffect(() => {
        if (searchQuery) {
            setCurrentKeyword(searchQuery);
            latestKeywordRef.current = searchQuery;
        }
    }, [searchQuery]);

    useEffect(() => {
        latestKeywordRef.current = currentKeyword;
    }, [currentKeyword]);

    useEffect(() => {
        setCurrentHold(hold);
    }, [hold]);

    useEffect(() => {
        if (!historySelection) {
            return;
        }

        const restoredQuery = typeof historySelection.query === 'string' ? historySelection.query.trim() : '';
        const restoredProducts = Array.isArray(historySelection.products) ? historySelection.products : [];
        if (!restoredQuery) {
            return;
        }

        setCurrentKeyword(restoredQuery);
        latestKeywordRef.current = restoredQuery;

        if (restoredProducts.length > 0) {
            setSearchResults(restoredProducts);
            previousProcessedKeywordRef.current = restoredQuery;
            setLoading(false);
            if (!isDataLoaded) {
                setIsDataLoaded(true);
            }
            return;
        }

        // Query-only restore path: force a fetch so restored history can reopen products.
        previousProcessedKeywordRef.current = '';
    }, [historySelection, isDataLoaded, setIsDataLoaded, setLoading]);

    const fetchSearchResults = React.useCallback(async () => {
        const normalizedKeyword = typeof currentKeyword === 'string' ? currentKeyword.trim() : '';
        if (!normalizedKeyword) {
            return;
        }

        if (normalizedKeyword === previousProcessedKeywordRef.current) {
            return;
        }

        if (isRequestInProgressRef.current) {
            return;
        }

        isRequestInProgressRef.current = true;
        setLoading(true);

        try {
            const results = await getSearchResults(
                normalizedKeyword,
                apiBaseUrl,
                clientKey,
                accessKey,
                accessSecret
            );

            previousProcessedKeywordRef.current = normalizedKeyword;

            if (results && Array.isArray(results) && results.length > 0) {
                setSearchResults(results);
                onResultsReady?.(normalizedKeyword, results);

                try {
                    sendRecoSearch(normalizedKeyword, results);
                } catch (_) {
                }

                if (!isDataLoaded) {
                    setIsDataLoaded(true);
                }
            } else {
                setSearchResults([]);
                onResultsReady?.(normalizedKeyword, []);
                if (!isDataLoaded) {
                    setIsDataLoaded(true);
                }
            }
        } catch (error) {
            console.error("Error fetching search results:", error);
            setSearchResults([]);
            onResultsReady?.(normalizedKeyword, []);

            previousProcessedKeywordRef.current = normalizedKeyword;
            if (!isDataLoaded) {
                setIsDataLoaded(true);
            }
        } finally {
            setLoading(false);
            isRequestInProgressRef.current = false;
        }
    }, [
        currentKeyword,
        apiBaseUrl,
        clientKey,
        accessKey,
        accessSecret,
        isDataLoaded,
        setIsDataLoaded,
        setLoading,
        previousProcessedKeywordRef,
        isRequestInProgressRef,
        onResultsReady
    ]);

    useEffect(() => {
        const canFetchForKeyword = currentHold === false || currentHold === null || typeof currentHold === 'undefined';
        if (currentKeyword && currentKeyword.length > 0 && canFetchForKeyword) {
            if (currentKeyword === previousProcessedKeywordRef.current) {
                return;
            }
            if (isRequestInProgressRef.current) {
                return;
            }
            fetchSearchResults();
        }
    }, [
        currentKeyword,
        currentHold,
        fetchSearchResults
    ]);

    if (loading) {
        return (
            <div
                ref={containerRef}
                className="relative w-full bg-[#F9F9F9]"
                style={{height: `${containerHeight}px`}}
            >
                <LoadingSpinner
                    size={150}
                    centered={true}
                    containerClassName="inset-0"
                />
            </div>
        );
    }

    return (
        <div ref={containerRef} className="flex justify-center items-center">
            {isDataLoaded && (
                <div className="bg-[#F9F9F9]">
                    {searchResults.length > 0 ? (
                        <ProductGrid
                            products={searchResults}
                            hasScrolled={hasScrolled}
                            setHasScrolled={setHasScrolled}
                            currentQuery={currentKeyword}
                            clientKey={clientKey}
                            currencyCode={currencyCode}
                            roundingEnabled={roundingEnabled}
                        />
                    ) : (
                        <div className="flex justify-center items-center w-full">
                            <div className="text-center p-8">
                                <h2 className="text-2xl font-bold text-gray-700 mb-4">No Results Found</h2>
                                <p className="text-gray-500">We couldn't find any products matching your search.</p>
                                <p className="text-gray-500">Please try a different search term.</p>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchResultsContainer;